/**
 * @Author: Junaid Atari junaid.attari@invozone.dev
 * @Date: 2025-02-18 14:52:39
 */

import {mixed, string} from 'yup';

// types
import type {Intent} from '~/types/shopify.types';
import type {FieldInfo, HandleFields} from '~/types/field.types';

export interface HandleProps<TFields> {
  /**
   * An intent object provided by the shopify bridge api
   */
  intent?: Intent;

  /**
   * A callback function to be called when the form is submitted
   * @param fields The form fields
   */
  onComplete?(fields: Partial<TFields>): void;
}

export const EXCLUDED_FIELDS: (keyof HandleFields)[] = [
  'select_template',
  'select_date',
  'first_name',
  'last_name',
  'offer_percentage',
  'company_name',
  'address_1',
  'address_2',
  'city',
  'state',
  'zip',
];

export const EXCLUDED_CATEGORIES: FieldInfo['category'][] = [
  'propertyAddress',
  'customField',
  'sender',
];

type PlaceholderVariable =
  | 'displayName'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address1'
  | 'address2'
  | 'city'
  | 'province'
  | 'zip'
  | 'country'
  | 'avatar'

type CustomerField = `customer.${PlaceholderVariable}`;


export const PLACEHOLDER_VARIABLES: CustomerField[] = [
  'customer.displayName',
  'customer.firstName',
  'customer.lastName',
  'customer.email',
  'customer.phone',
  'customer.address1',
  'customer.address2',
  'customer.city',
  'customer.province',
  'customer.zip',
  'customer.country',
  'customer.avatar',
];

/**
 * @public
 * @static
 * Splits fields into columns of a given size and returns an array of columns.
 * @param fields - An array of field objects.
 * @param size - The size of the columns
 * @returns An array of arrays, where each inner array is a column of the original array.
 */
export const splitFieldsIntoColumns = (
  fields: FieldInfo[], size: number = 2,
): Array<FieldInfo[]> => {
  const columns: Array<FieldInfo[]> = [];
  for (let i = 0; i < fields.length; i += size) {
    columns.push(fields.slice(i, i + size));
  }
  return columns;
};

/**
 * @public
 * @static
 * Cast every form value into a string
 * @param fields - The form fields data
 * @returns The casted values
 */
export const castFormValues = (fields: Record<string, any>): Record<string, string | undefined> => {
  if (!Object.keys(fields).length) return {};

  const newFields = {} as Record<string, string | undefined>;

  for (const [key, value] of Object.entries(fields)) {
    const casted = String(value || '');
    newFields[key] = casted.length > 0 ? casted : undefined;
  }

  return newFields;
};

/**
 * Checks that given value or values are empty or not<br>
 * <b>Note:</b> `null`, `undefined` or `empty` (after trimming whitespaces) values are considered empty.
 * @param value The value or values to check
 * @returns True if value(s) are empty, false otherwise
 */
export const isEmpty = (value: any | any[]): boolean => {
  let values = Array.isArray(value)
    ? value : [value];

  for (const val of values) {
    if (!val || String(val || '').trim().length === 0) return true;
  }

  return false;
};

/**
 * Check the give value is a valid placeholder variable or not
 * @param value The value to check
 * @return True if the value is a valid, false otherwise
 */
export const isPlaceholderVariable = (value: string) => {
  if (!/^\{customer\.[a-zA-Z]+}$/.test(value)) {
    return false;
  }

  return PLACEHOLDER_VARIABLES.includes(value as any);
};

export const genericFieldsSchema = {
  select_template: string().required('Template is required'),
  select_date: string().required('Schedule date is required'),
  first_name: string().min(3, 'First name should be greater than 3 chars').nullable(),
  last_name: string().min(3, 'Last name should be greater than 3 chars').nullable(),
  company_name: string().min(3, 'Company name should be greater than 3 chars')
    .when(['first_name', 'last_name'], (values, schema) => {
        return isEmpty(values) ? schema.required('Either Name or Company name is required') : schema;
      },
    ).nullable(),
  email: string()
    .test(
      'email-or-regex',
      'Must be a valid email or a placeholder value',
      value => {
        if (!value || value?.trim?.()) return true;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const customRegex = /^\{customer\.email}$/;

        return emailRegex.test(value) || customRegex.test(value);
      }
    ),
  phone_no: string()
    .test(
      'phone-or-regex',
      'Must be a phone number or a placeholder value',
      value => {
        if (!value || value?.trim?.()) return true;

        const phoneRegex = /^\d{5,}$/;
        const customRegex = /^\{customer\.phone}$/;

        return phoneRegex.test(value) || customRegex.test(value);
      }
    ),
  address_1: string().min(5, 'Address should be greater than 5 chars').required('Address Line 1 is required'),
  address_2: string().min(5, 'Address should be greater than 5 chars').optional(),
  city: string().min(3, 'City should be greater than 3 chars').required('City is required'),
  state: string().min(2, 'State should be greater than 2 chars').required('State is required'),
  zip: mixed().test(
    'to-zip-is-string-or-number',
    'Zip code must be a placeholder variable or a number',
    (value: any) => {
      if (typeof value === 'string' && (value === '{customer.zip}' || /^\d+$/.test(value))) {
        return true;
      }

      if (typeof value === 'string' && /^\d+-\d+$/.test(value)) {
        return true;
      }
      return typeof value === 'number';
    },
  ).required('Zip Code is required'),
  from_zip: mixed().test(
    'from-zip-is-string-or-number',
    'Zip code must be a placeholder variable or a number',
    (value: any) => {
      if (!value) return true;

      if (typeof value === 'string' && (value === '{customer.zip}' || /^\d+$/.test(value))) {
        return true;
      }

      if (typeof value === 'string' && /^\d+-\d+$/.test(value)) {
        return true;
      }
    },
  ).nullable(),
  property_zip_code: mixed().test(
    'property-zip-is-string-or-number',
    'Zip code must be a placeholder variable or a number',
    (value: any) => {
      if (!value) return true;

      if (typeof value === 'string' && /^\d+-\d+$/.test(value)) {
        return true;
      }

      if (typeof value === 'string' && (value === '{customer.zip}' || /^\d+$/.test(value))) {
        return true;
      }
    },
  ).nullable(),
};
